/**
 * Formate un prix en format français
 * @param {number} price - Le prix à formater
 * @param {string} currency - La devise (par défaut 'EUR')
 * @returns {string} Le prix formaté
 */
export const formatPrice = (price, currency = 'EUR') => {
  if (typeof price !== 'number' || isNaN(price)) {
    return '0,00 €';
  }

  // Arrondir à 2 décimales pour éviter les erreurs d'arrondi
  const roundedPrice = Math.round(price * 100) / 100;
  
  // Formater le prix avec 2 décimales et virgule comme séparateur décimal
  const formattedPrice = roundedPrice.toFixed(2).replace('.', ',');
  
  // Ajouter le symbole de la devise
  switch (currency.toUpperCase()) {
    case 'EUR':
      return `${formattedPrice} €`;
    case 'USD':
      return `$${formattedPrice}`;
    case 'GBP':
      return `£${formattedPrice}`;
    default:
      return `${formattedPrice} ${currency}`;
  }
};

/**
 * Formate un prix pour l'affichage dans les cartes et listes
 * @param {number} price - Le prix à formater
 * @param {string} currency - La devise (par défaut 'EUR')
 * @returns {string} Le prix formaté
 */
export const formatPriceCompact = (price, currency = 'EUR') => {
  if (typeof price !== 'number' || isNaN(price)) {
    return '0€';
  }

  // Si le prix est un nombre entier, ne pas afficher les décimales
  if (Number.isInteger(price)) {
    switch (currency.toUpperCase()) {
      case 'EUR':
        return `${price}€`;
      case 'USD':
        return `$${price}`;
      case 'GBP':
        return `£${price}`;
      default:
        return `${price} ${currency}`;
    }
  }

  // Sinon, formater avec 2 décimales
  return formatPrice(price, currency);
};

/**
 * Formate un prix pour l'affichage des pourcentages
 * @param {number} percentage - Le pourcentage à formater
 * @returns {string} Le pourcentage formaté
 */
export const formatPercentage = (percentage) => {
  if (typeof percentage !== 'number' || isNaN(percentage)) {
    return '0%';
  }

  return `${percentage.toFixed(1)}%`;
};

/**
 * Formate un montant pour l'affichage des soldes
 * @param {number} amount - Le montant à formater
 * @param {string} currency - La devise (par défaut 'EUR')
 * @returns {string} Le montant formaté
 */
export const formatBalance = (amount, currency = 'EUR') => {
  if (typeof amount !== 'number' || isNaN(amount)) {
    return '0,00 €';
  }

  // Si le montant est négatif, ajouter le signe moins
  if (amount < 0) {
    return `-${formatPrice(Math.abs(amount), currency)}`;
  }

  return formatPrice(amount, currency);
};

/**
 * Formate un nombre avec séparateurs de milliers
 * @param {number} number - Le nombre à formater
 * @returns {string} Le nombre formaté
 */
export const formatNumber = (number) => {
  if (typeof number !== 'number' || isNaN(number)) {
    return '0';
  }

  return new Intl.NumberFormat('fr-FR').format(number);
};

/**
 * Alias pour formatPrice - Formate un montant en devise
 * @param {number} amount - Le montant à formater
 * @param {string} currency - La devise (par défaut 'EUR')
 * @returns {string} Le montant formaté
 */
export const formatCurrency = (amount, currency = 'EUR') => {
  return formatPrice(amount, currency);
};













